/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isEmpty from 'lodash.isempty';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {EditorMode} from '@ion/core/src/types/editors';
import {
  Device,
  DeviceFile,
  Fields,
  GenericObject,
  PlatformDeviceType
} from '@ion/core/src/types/entities';
import {TemperatureType} from '@ion/core/src/types/general';
import {fahrenheitToCelsius} from '@ion/core/src/utils/Conversions';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {LabelContentType} from '../types/types';
import {SCIDeviceSettingsFormField} from './DeviceEditorUtils';
import {DEVICE_FILE_FIELD} from './DeviceFileDynamicFields';

export interface SCIDeviceSettingsValidatorProps {
  name: string,
  value: number,
  temperatureType?: string,
  firstValue?: number,
  secondValue?: number
}

export interface CheckInheritableStatusParam {
  ancestorsGroups: GenericObject,
  groupAccountId: number;
}

enum InheritableRelation {
  SUB_ACCOUNT = 'subAccount'
};

// helper method helps to determines  mode...
const isMode = (editorMode: EditorMode) => {
  return (mode: EditorMode) => mode === editorMode;
};

// determines whether the passing mode is clone or not...
export const isCloneMode = isMode(EditorMode.Clone);

// determines whether the passing mode is edit or not...
export const isEditMode = isMode(EditorMode.Edit);

// determines whether the passing mode is view or not...
export const isViewMode = isMode(EditorMode.View);

/**
 * The utility returns true when the menu needs to update it's options by calling CTC
 * api, else returns false to hold the updation.
 *
 * ```typescript
 *
 *  import {isNeedToUpdateOptions} '@ion/dls/src/editors/helpers.ts';
 *
 *  const isNeedToUpdate = isNeedToUpdateOptions(options, 'mkumar');
 *
 * ```
 */
export const isNeedToUpdateOptions = (options: Array<any>, searchTerm: string | number): boolean => {
  return searchTerm !== null && searchTerm !== 0 && !options?.find(
    ({label, value}) => value === searchTerm || label === searchTerm
  );
};

const validateTemperature = ({
  name,
  value,
  temperatureType,
  firstValue,
  secondValue
}: SCIDeviceSettingsValidatorProps): boolean => {
  const temp = temperatureType === TemperatureType.Fahrenheit ? fahrenheitToCelsius(value) : value;
  const minTemp = temperatureType === TemperatureType.Fahrenheit ? fahrenheitToCelsius(firstValue) : firstValue;
  const maxTemp = temperatureType === TemperatureType.Fahrenheit ? fahrenheitToCelsius(secondValue) : secondValue;
  switch (name) {
    case SCIDeviceSettingsFormField.MinTemperature :
      return (temp >= -29) && (temp <= 60) && (temp < maxTemp);
    case SCIDeviceSettingsFormField.MaxTemperature :
      return (temp >= -29) && (temp <= 60) && (temp > minTemp);
    default :
      return false;
  };
};

const validateTilt = (value: number): boolean => value >= 0 && value <= 90;

const validateImpact = (value: number): boolean => value >= 0 && value <=16;

const validateEmptyField = (value: number): boolean => !isNaN(value);

/**
* handleSCISettingsValidation is a utility function validate
* user input on SCI device settings
*
*```typescript
*
*   import {handleSCISettingsValidation} from '@ion/dls/src/editors/helpers.ts';
*   ...
*   const isValid = handleSCISettingsValidation({name, value, temperatureType, firstValue, secondValue});
*   ...
*
*```
**/
export const handleSCISettingsValidation = (
  {
    name,
    value,
    temperatureType,
    firstValue,
    secondValue
  }: SCIDeviceSettingsValidatorProps): boolean => {
  const isNotEmpty = validateEmptyField(value);
  const isTemperatureValid = (name === SCIDeviceSettingsFormField.MinTemperature ||
    name === SCIDeviceSettingsFormField.MaxTemperature)
    ? validateTemperature({name, value, temperatureType, firstValue, secondValue}) : true;
  const isTiltValid = name === SCIDeviceSettingsFormField.MaxTip
    ? validateTilt(value) : true;
  const isImpactValid = name === SCIDeviceSettingsFormField.MaxImpact
    ? validateImpact(value) : true;

  return isNotEmpty && isTemperatureValid && isTiltValid && isImpactValid;
};

const checkDeviceTypeId = (deviceTypes, deviceTypeId): boolean => {
  return isEmpty(deviceTypes) ? false : !!deviceTypes?.find(({id}) => id === deviceTypeId);
};

const filter = (items, id) => {
  return items?.filter(type => type?.id === id) || [];
};

/**
 * Based on the provided `DeviceFile` and `DeviceType`, determines if support for downloading
 * is supported based on the file metadata device type id being present within the collection
 * of file types, and the file type for that particular device type having it's `downloadable`
 * property set to `true`, otherwise, returns `false`.
 *
 * ```typescript
 *  const isDownloadable = canDownloadDeviceFiles(file, type);
 * ```
 *
 * @param   file The Device File which is to be used for determining the device type.
 * @param   type The Device Type for the given Device File
 * @returns true if the Device Type for the provided Device File's `downloadable`
 *          property is set to `true`, otherwise, `false`.
 *
 */
export const canDownloadDeviceFiles = (file: DeviceFile, type: PlatformDeviceType): boolean => {
  if (file && type) {
    const deviceTypeId = file.validationMetaData?.deviceTypeId;
    const fileTypeId   = file.validationMetaData?.fileTypeId;
    if (deviceTypeId) {
      if (checkDeviceTypeId(type, deviceTypeId)) {
        const filtered = filter(type, deviceTypeId);
        if (filtered.length) {
          const types = filter(filtered[0]?.fileTypes, fileTypeId);
          return !!types[0]?.downloadable;
        }
      }
    }
  }
  return false;
};

/**
 * Defines a utility to return the appropriate labels for the LabelChipChooser.
 *
 * ```typescript
 * import {getInitialSelectedLabels} from '../helpers';
 *
 * const labelContent = {
 *  primaryLabel   : 'assetName',
 * secondaryLabel : null
 * };
 *
 * const selectedLabels = getInitialSelectedLabels(labelContent);
 *
 * ```
 *
 * @param value The value that will be used to display the label(s).
 * @returns {array} The label(s) for the LabelChipChooser.
 */
export const getInitialSelectedLabels = (value: string | LabelContentType): Array<string> => {
  if (!value) {
    return [];
  }
  if (typeof value === 'string') {
    return [value];
  }
  if ('primaryLabel' in value && 'secondaryLabel' in value) {
    return [value.primaryLabel, value.secondaryLabel];
  }
  return [];
};

/**
 * Defines a utility to return the available device data.
 *
 * ```typescript
 *  const device = getSelectedDevice(selectedDevice, deviceDetail);
 * ```
 *
 * @param   device The Device Type to retrieve device identifiers.
 * @param   deviceDetail The Device Detail Type to retrieve device identifiers
 * @returns available device data.
 *
 */
export const getSelectedDevice = (device: Device, deviceDetail: Device): Device => {
  return isObjectEmpty(device) ? deviceDetail : device || deviceDetail;
};

/**
 * Defines a utility to return `true` when child account able to see parent
 * account in the grid otherwise,, `false`.
 *
 * ```typescript
 *  getInheritableStatus(accountsMap, groupAccountId, globalAccountId);
 * ```
 *
 * @param accountsMap The hierarchy of accounts.
 * @param groupAccountId The account id of each group.
 * @param globalAccountId The current group id.
 * @returns available device data.
 *
 */
export const getInheritableStatus = (
  accountsMap: GenericObject,
  groupAccountId: number,
  globalAccountId: number
): boolean => {
  if (accountsMap?.[groupAccountId]?.rel === InheritableRelation.SUB_ACCOUNT) {
    return false;
  }
  return groupAccountId !== globalAccountId;
};

/**
 * Constructs a label based on the given params to return value & description
 * when field.name is APP_ID otherwise description or falls back to value when
 * description is not available.
 *
 * ```typescript
 *  getLabel(value, description, field);
 * ```
 *
 */
export const getLabel = (value: string, description: string, field): string => {
  if (field.name === DEVICE_FILE_FIELD.APP_ID) {
    return `${value || ''}${(value && description) ? ' - ' : ''}${description || ''}`;
  }
  return description ?? value;
};

/**
 * Maps over an array of deviceFileAppId items and returns a formatted label
 * based on the value and description fields. If withDescription is `true`, it
 * includes both value and desciption otherwise only value is included.
 *
 * ```typescript
 *  getDeviceFileAppId(deviceFileAppId, withDescription);
 * ```
 *
 */
export const getDeviceFileAppId = (
  deviceFileAppId: Fields,
  withDescription: boolean = false
) => {
  return deviceFileAppId.map(({
    value, description
  }) => withDescription ? `${value ?? ''} - ${description ?? ''}` : value ?? '').join(', ');
};

/**
 * Maps over an array of defaultValue items and returns an array of objects.
 *
 * ```typescript
 *  getSelectedValue(defaultValue);
 * ```
 *
 */
export const getSelectedValue = (defaultValue) => {
  return defaultValue.map(({id, name}) => ({id: id ?? '', value: id ?? '', label: name ?? ''}));
};

/**
 * Maps over an array of accountTree items and returns an array of objects..
 *
 * ```typescript
 *  getOptionsData(accountTree);
 * ```
 *
 */
export const getOptionsData = (accountTree) => {
  return accountTree.map(item => ({...item, name: item.title ?? '', id: entityId(item) ?? ''}));
};

/**
 * Defines a utility that returns `true` if the child account is present in the
 * ancestor accounts array; otherwise, it returns `false`.
 *
 * ```typescript
 *  checkInheritableStatus(ancestorsGroups, groupAccountId);
 * ```
 *
 * @param ancestorsGroups List of ancestor accounts for the currently selected Global Account.
 * @param groupAccountId The account id of each group.
 * @returns a boolean value indicating whether groupAccountId is present in ancestorsGroups.
 *
 */
export const checkInheritableStatus = ({
  ancestorsGroups = [],
  groupAccountId
}: CheckInheritableStatusParam): boolean => {
  const ancestorsAccountsIds = ancestorsGroups?.map((account) => entityId(account));
  return ancestorsAccountsIds?.includes(groupAccountId) || false;
};
